import { QueryClient } from "@tanstack/react-query";

type CalendarQueryKey = readonly ["calendar"] | readonly ["calendar", object];
type InboxQueryKey = readonly ["inbox"] | readonly ["inbox", object];
type BlockQueryKey = CalendarQueryKey | InboxQueryKey;

type InvalidateRequest = {
  queryKey: BlockQueryKey;
};

interface EnqueueOptions {
  isError?: boolean;
  isIdNeeded?: boolean;
}

// ─── 싱글톤 디바운스 Invalidate 클래스 ──────────────────────────────
class DebouncingInvalidateQueue {
  private static instance: DebouncingInvalidateQueue;
  private queryClient: QueryClient | null = null;

  private queue: InvalidateRequest[] = [];
  private processedKeys = new Set<string>();
  private timeoutId: NodeJS.Timeout | null = null;
  private isProcessing = false;
  private readonly delay = 4000;

  private constructor() {}

  public static getInstance(): DebouncingInvalidateQueue {
    if (!DebouncingInvalidateQueue.instance) {
      DebouncingInvalidateQueue.instance = new DebouncingInvalidateQueue();
    }
    return DebouncingInvalidateQueue.instance;
  }

  public setQueryClient(client: QueryClient) {
    this.queryClient = client;
  }

  private isQueryKeyMatching(key1: BlockQueryKey, key2: BlockQueryKey): boolean {
    return (
      JSON.stringify(key1) === JSON.stringify(key2) ||
      (key1.length === 1 && key2.length > 1 && key1[0] === key2[0])
    );
  }

  public async enqueueInvalidate(queryKey: BlockQueryKey, options?: EnqueueOptions) {
    if (!this.queryClient) {
      console.error("QueryClient가 설정되지 않았습니다. setQueryClient()를 먼저 호출하세요.");
      return;
    }

    const keyAsString = JSON.stringify(queryKey);

    const shouldInvalidateImmediately = options?.isError || options?.isIdNeeded;

    if (shouldInvalidateImmediately) {
      await this.queryClient.invalidateQueries({ queryKey });
      this.processedKeys.add(keyAsString);
      this.queue = this.queue.filter((item) => !this.isQueryKeyMatching(queryKey, item.queryKey));
      return;
    }

    const alreadyQueued = this.queue.some((item) => JSON.stringify(item.queryKey) === keyAsString);

    if (!alreadyQueued) {
      this.queue.push({ queryKey });
    }

    // 디바운싱 처리 (타이머는 enqueue 시점에서만!)
    if (this.timeoutId) clearTimeout(this.timeoutId);

    this.timeoutId = setTimeout(async () => {
      if (this.isProcessing) {
        // 병합 중이면 다시 delay 후 재시도
        this.timeoutId = setTimeout(() => this.processQueue(), this.delay);
        return;
      }
      await this.processQueue();
    }, this.delay);
  }

  private async processQueue() {
    if (!this.queryClient || this.queue.length === 0 || this.isProcessing) return;

    this.isProcessing = true;

    const uniqueKeys = new Set<string>();
    const groupedKeys = new Set<string>();

    for (const { queryKey } of this.queue) {
      if (queryKey.length === 1) {
        groupedKeys.add(queryKey[0] as string);
      }
    }

    while (this.queue.length > 0) {
      const { queryKey } = this.queue.shift()!;
      const keyAsString = JSON.stringify(queryKey);

      if (
        this.processedKeys.has(keyAsString) ||
        (queryKey.length > 1 && groupedKeys.has(queryKey[0] as string)) ||
        uniqueKeys.has(keyAsString)
      ) {
        continue;
      }

      uniqueKeys.add(keyAsString);

      try {
        await this.queryClient.cancelQueries({ queryKey });
        await this.queryClient.invalidateQueries({ queryKey, refetchType: "all" });
      } catch (e) {
        console.error("invalidate 실패:", e);
      }
    }

    this.processedKeys.clear();
    this.timeoutId = null;
    this.isProcessing = false;
  }
}

export const debounceInvalidate = DebouncingInvalidateQueue.getInstance();
